import {
  Cushion,
  FlexContainer,
  Grid,
  Rectangle,
  Shadow,
  Typography,
  withTheme,
} from '@pitchero/react-ui';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { isNull } from 'lodash';
import { Trans } from '../../lib/i18n';
import MatchEventIcon from './match-event-icon';
import getMatchEventTime from './match-event-time';
import { isHomeTeamEvent } from './helpers';
import { matchCommentaryScoreLineEvents } from './constants';

const CommentaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 10px;

  & .commentaryTime {
    justify-self: center;
    font-weight: bold;
    font-size: 18px;
    position: relative;
    width: 100%;
    text-align: center;
  }
  // Styles common to all lines
  & .commentaryTime::before,
  & .commentaryTime::after,
  & .commentaryEvent::before {
    content: ' ';
    position: absolute;
    width: 2px;
    background-color: black;
  }

  // Show top line on all but first commentary times
  & .commentary:not(:first-of-type) .commentaryTime::before {
    left: calc(50% - 1px);
    top: -200%;
    bottom: 100%;
  }

  // Show bottom line on all but last commentary times
  & .commentary:not(:last-of-type) .commentaryTime::after {
    left: calc(50% - 1px);
    top: 100%;
    bottom: -200%;
  }

  & .commentaryEvent {
    position: relative;
    border-radius: 10px;
  }
  // Show horizontal line on all events
  & .commentaryEvent::before {
    top: 50%;
    transform: translate(-100%, -2px);
    width: 18px;
    height: 2px;
  }
`;

const MatchCommentary = ({ events, uiTheme }) => (
  <Rectangle
    fill="white"
    style={{ border: '1px solid #d9d9d9', overflow: 'hidden' }}
    responsive={[{ minWidth: 'fullHeader', props: { radius: 'rounded' } }]}
  >
    <Rectangle fill="#fafafa" borderBottom="1px solid #d9d9d9">
      <Cushion all="small">
        <Typography size={16} weight="bold" color="black" align="center" component="h3">
          <Trans i18nKey="teams:match_commentary">Match Commentary</Trans>
        </Typography>
      </Cushion>
    </Rectangle>
    <CommentaryContainer>
      {events
        .flatMap((event) => event.events)
        .filter((event) => !['star player', 'clean sheet'].includes(event.eventType.toLowerCase()))
        .map((event) => {
          const isOurTeam = isHomeTeamEvent(event);
          return (
            <Grid columns="30px 1fr" columnGap="20px" alignItems="center" className="commentary">
              <div className="commentaryTime">{getMatchEventTime(event.time)}</div>
              <Shadow shadow="dark" className="commentaryEvent">
                <div className="commentaryLine" />
                <Rectangle
                  radius="10px"
                  style={{
                    width: '100%',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                  }}
                >
                  <FlexContainer flexDirection="column">
                    <FlexContainer
                      justifyContent="space-between"
                      style={{
                        background: isOurTeam ? uiTheme?.colors?.primary : 'grey',
                        color: isOurTeam ? uiTheme?.colors?.primaryContrast : 'white',
                        padding: '10px',
                      }}
                    >
                      <Typography transform="uppercase">{event.eventType}</Typography>
                      <Typography>
                        {matchCommentaryScoreLineEvents.includes(event.eventType) &&
                          !isNull(event.homeScore) &&
                          !isNull(event.awayScore) &&
                          `${event.homeScore} - ${event.awayScore}`}
                      </Typography>
                    </FlexContainer>
                    <FlexContainer alignItems="center" style={{ gap: '10px', padding: '10px' }}>
                      <MatchEventIcon {...event.icon} size={24} />
                      {event.commentary}
                    </FlexContainer>
                  </FlexContainer>
                </Rectangle>
              </Shadow>
            </Grid>
          );
        })}
    </CommentaryContainer>
  </Rectangle>
);

MatchCommentary.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  uiTheme: PropTypes.shape({
    colors: PropTypes.shape({
      primary: PropTypes.string,
      primaryContrast: PropTypes.string,
    }),
  }).isRequired,
};

export default withTheme(MatchCommentary);
